import { useState } from "react";

// ─── DATA ──────────────────────────────────────────────────────────────────────

const STATES = [
  "Alabama","Alaska","Arizona","Arkansas","California","Colorado","Connecticut",
  "Delaware","Florida","Georgia","Hawaii","Idaho","Illinois","Indiana","Iowa",
  "Kansas","Kentucky","Louisiana","Maine","Maryland","Massachusetts","Michigan",
  "Minnesota","Mississippi","Missouri","Montana","Nebraska","Nevada","New Hampshire",
  "New Jersey","New Mexico","New York","North Carolina","North Dakota","Ohio",
  "Oklahoma","Oregon","Pennsylvania","Rhode Island","South Carolina","South Dakota",
  "Tennessee","Texas","Utah","Vermont","Virginia","Washington","West Virginia",
  "Wisconsin","Wyoming"
];

const MOVE_MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const PEAK_MONTHS = [5, 6, 7, 8]; // June–Sep

// Rough cost-of-living index by state (US avg = 100)
const COL_INDEX = {
  "California": 151, "New York": 139, "Hawaii": 184, "Massachusetts": 135,
  "Connecticut": 127, "New Jersey": 125, "Maryland": 124, "Washington": 118,
  "Oregon": 115, "Colorado": 112, "Illinois": 105, "Virginia": 108,
  "Minnesota": 103, "Pennsylvania": 102, "Florida": 103, "Arizona": 102,
  "Nevada": 104, "Georgia": 96, "North Carolina": 94, "Texas": 93,
  "Tennessee": 90, "Ohio": 90, "Indiana": 89, "Michigan": 91,
  "Wisconsin": 95, "Missouri": 88, "Kansas": 87, "Iowa": 89,
  "Nebraska": 92, "Oklahoma": 85, "Arkansas": 84, "Mississippi": 83,
  "Alabama": 86, "South Carolina": 92, "Kentucky": 87, "Louisiana": 91,
  "New Mexico": 90, "Idaho": 98, "Utah": 105, "Montana": 100,
  "Wyoming": 95, "North Dakota": 98, "South Dakota": 93, "Vermont": 114,
  "New Hampshire": 115, "Maine": 107, "Delaware": 107, "Rhode Island": 116,
  "Alaska": 127, "West Virginia": 87
};

// State geographic centroids [lat, lng]
const STATE_COORDS = {
  "Alabama":        [32.8,  -86.8], "Alaska":         [64.2, -153.4],
  "Arizona":        [34.3, -111.1], "Arkansas":       [34.9,  -92.4],
  "California":     [36.8, -119.4], "Colorado":       [39.0, -105.5],
  "Connecticut":    [41.6,  -72.7], "Delaware":       [39.0,  -75.5],
  "Florida":        [28.6,  -82.4], "Georgia":        [32.7,  -83.6],
  "Hawaii":         [20.9, -157.0], "Idaho":          [44.4, -114.5],
  "Illinois":       [40.0,  -89.2], "Indiana":        [39.9,  -86.3],
  "Iowa":           [42.1,  -93.5], "Kansas":         [38.5,  -98.4],
  "Kentucky":       [37.5,  -85.3], "Louisiana":      [31.2,  -91.8],
  "Maine":          [45.4,  -69.2], "Maryland":       [39.1,  -76.8],
  "Massachusetts":  [42.3,  -71.8], "Michigan":       [44.3,  -85.4],
  "Minnesota":      [46.4,  -93.1], "Mississippi":    [32.7,  -89.7],
  "Missouri":       [38.5,  -92.5], "Montana":        [47.0, -110.0],
  "Nebraska":       [41.5,  -99.9], "Nevada":         [38.5, -117.1],
  "New Hampshire":  [43.7,  -71.6], "New Jersey":     [40.1,  -74.5],
  "New Mexico":     [34.5, -106.1], "New York":       [42.9,  -75.5],
  "North Carolina": [35.6,  -79.8], "North Dakota":   [47.5, -100.5],
  "Ohio":           [40.4,  -82.8], "Oklahoma":       [35.6,  -97.5],
  "Oregon":         [44.1, -120.5], "Pennsylvania":   [40.6,  -77.2],
  "Rhode Island":   [41.7,  -71.5], "South Carolina": [33.9,  -80.9],
  "South Dakota":   [44.4, -100.2], "Tennessee":      [35.9,  -86.7],
  "Texas":          [31.5,  -99.3], "Utah":           [39.3, -111.1],
  "Vermont":        [44.0,  -72.7], "Virginia":       [37.8,  -78.2],
  "Washington":     [47.4, -120.5], "West Virginia":  [38.6,  -80.6],
  "Wisconsin":      [44.3,  -89.6], "Wyoming":        [43.0, -107.6]
};

const PHASE_COLORS = {
  "Research":    "#3B82F6", "Planning":   "#8B5CF6",
  "Admin":       "#F59E0B", "Real Estate":"#10B981",
  "Housing":     "#10B981", "Packing":    "#EF4444",
  "Vehicle":     "#06B6D4", "Storage":    "#F97316",
  "Move Day":    "#EC4899", "Post-Move":  "#6B7280"
};

const TASK_TEMPLATES = {
  base: [
    { id: "t1",  phase: "Research",  title: "Research neighborhoods in destination",       detail: "Compare commute, schools, safety, walkability",                    weeks: 12 },
    { id: "t2",  phase: "Research",  title: "Get 3+ moving company quotes",                detail: "Compare full-service, PODS, and truck rental options",             weeks: 10 },
    { id: "t3",  phase: "Planning",  title: "Set your moving budget",                      detail: "Include truck, packing, deposits, travel, and buffer",             weeks: 10 },
    { id: "t4",  phase: "Planning",  title: "Create a moving binder / folder",             detail: "Track quotes, contacts, receipts, and important docs",             weeks: 9  },
    { id: "t5",  phase: "Admin",     title: "Notify employer / HR of address change",      detail: "Update payroll, benefits, and remote work arrangements",           weeks: 8  },
    { id: "t6",  phase: "Packing",   title: "Start decluttering — donate or sell",         detail: "Less stuff = lower moving cost. Start with closets.",              weeks: 8  },
    { id: "t7",  phase: "Admin",     title: "Forward mail via USPS",                       detail: "Schedule mail forwarding to start on move date",                   weeks: 6  },
    { id: "t8",  phase: "Admin",     title: "Notify banks and financial institutions",     detail: "Update billing address on all accounts",                           weeks: 6  },
    { id: "t9",  phase: "Packing",   title: "Order packing supplies",                     detail: "Boxes, tape, bubble wrap, markers, labels",                        weeks: 6  },
    { id: "t10", phase: "Packing",   title: "Pack non-essentials first",                   detail: "Seasonal items, books, decor, extra linens",                       weeks: 4  },
    { id: "t11", phase: "Admin",     title: "Transfer / update utilities",                 detail: "Cancel at origin, set up at destination: electric, gas, internet", weeks: 4  },
    { id: "t12", phase: "Admin",     title: "Update subscriptions and delivery addresses", detail: "Amazon, meal kits, magazines, etc.",                               weeks: 3  },
    { id: "t13", phase: "Packing",   title: "Pack remaining rooms",                        detail: "Leave out only essentials for move week",                          weeks: 2  },
    { id: "t14", phase: "Move Day",  title: "Final walkthrough of old home",               detail: "Check all rooms, closets, outdoor areas",                          weeks: 0  },
    { id: "t15", phase: "Post-Move", title: "Update driver's license and vehicle registration", detail: "Usually required within 30–90 days of moving",              weeks: -2 },
    { id: "t16", phase: "Post-Move", title: "Register to vote in new state",               detail: "Deadlines vary — do it early",                                    weeks: -2 },
    { id: "t17", phase: "Post-Move", title: "Find new doctors, dentists, specialists",     detail: "Transfer medical records before switching",                        weeks: -4 },
  ],
  own: [
    { id: "to1", phase: "Real Estate", title: "Interview real estate agents",              detail: "Get at least 2 listing agents for your origin home",               weeks: 16 },
    { id: "to2", phase: "Real Estate", title: "Get a pre-listing home inspection",         detail: "Fix issues before listing to avoid surprises",                     weeks: 14 },
    { id: "to3", phase: "Real Estate", title: "List your home for sale",                   detail: "Coordinate list date with expected purchase timeline",              weeks: 12 },
    { id: "to4", phase: "Real Estate", title: "Get pre-approved for mortgage at destination", detail: "Requirements may differ by state",                             weeks: 10 },
    { id: "to5", phase: "Real Estate", title: "Work with buyer's agent in destination",    detail: "Start virtual tours and narrowing neighborhoods",                  weeks: 8  },
  ],
  rent: [
    { id: "tr1", phase: "Housing", title: "Review your lease break terms",                 detail: "Check penalties, notice requirements, subletting rules",           weeks: 12 },
    { id: "tr2", phase: "Housing", title: "Send formal move-out notice to landlord",       detail: "Get it in writing. Note required notice period.",                  weeks: 10 },
    { id: "tr3", phase: "Housing", title: "Research rental market at destination",         detail: "Use Zillow, Apartments.com, Facebook Marketplace",                 weeks: 8  },
    { id: "tr4", phase: "Housing", title: "Apply for apartments / sign new lease",         detail: "Many landlords want 30–60 days lead time",                        weeks: 6  },
    { id: "tr5", phase: "Housing", title: "Schedule move-out inspection with current landlord", detail: "Document condition to protect your deposit",               weeks: 2  },
  ],
  vehicle: [
    { id: "tv1", phase: "Vehicle", title: "Get vehicle shipping quotes",                   detail: "Try Montway, AmeriFreight, uShip. Book early.",                    weeks: 10 },
    { id: "tv2", phase: "Vehicle", title: "Book vehicle transport company",                detail: "Confirm pickup and delivery windows",                              weeks: 8  },
    { id: "tv3", phase: "Vehicle", title: "Document vehicle condition before pickup",      detail: "Photos of all angles, existing damage",                            weeks: 1  },
  ],
  storage: [
    { id: "ts1", phase: "Storage", title: "Research storage units near destination",       detail: "Climate-controlled if storing electronics, wood furniture",        weeks: 8  },
    { id: "ts2", phase: "Storage", title: "Reserve storage unit",                          detail: "Book in advance — demand spikes near move dates",                  weeks: 6  },
    { id: "ts3", phase: "Storage", title: "Label boxes going to storage separately",       detail: "Color-code or use a numbering system",                             weeks: 3  },
  ]
};

// ─── HELPERS ───────────────────────────────────────────────────────────────────

function fmt(n) { return "$" + Math.abs(n).toLocaleString(); }
function getCOL(state) { return COL_INDEX[state] || 100; }

function haversineDistance(coord1, coord2) {
  const R = 3959; // miles
  const toRad = d => d * Math.PI / 180;
  const dLat = toRad(coord2[0] - coord1[0]);
  const dLon = toRad(coord2[1] - coord1[1]);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(coord1[0])) * Math.cos(toRad(coord2[0])) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function getMiles(origin, dest) {
  const c1 = STATE_COORDS[origin];
  const c2 = STATE_COORDS[dest];
  if (!c1 || !c2) return 500;
  return haversineDistance(c1, c2);
}

function estimateMovingCost(origin, dest, bedrooms, peakSeason, vehicleShip, storage) {
  const miles = getMiles(origin, dest);
  const baseByBed = { "1": 1000, "2": 1800, "3": 2800, "4": 4000, "5+": 5500 };
  let base = baseByBed[bedrooms] || 1800;

  let distMultiplier;
  if (miles < 100)       distMultiplier = 1.0;
  else if (miles < 300)  distMultiplier = 1.3;
  else if (miles < 600)  distMultiplier = 1.7;
  else if (miles < 1200) distMultiplier = 2.2;
  else                   distMultiplier = 2.8;

  base *= distMultiplier;
  if (peakSeason) base *= 1.3;

  const packing     = base * 0.2;
  const vehicle     = vehicleShip ? (miles > 500 ? 1400 : 900) : 0;
  const storageEst  = storage ? 200 : 0;

  return {
    miles:   Math.round(miles),
    moving:  Math.round(base / 100) * 100,
    packing: Math.round(packing / 50) * 50,
    vehicle,
    storage: storageEst,
    total:   Math.round((base + packing + vehicle + storageEst) / 100) * 100
  };
}

function getHousingDelta(origin, dest, ownership) {
  const originCOL  = getCOL(origin);
  const destCOL    = getCOL(dest);
  const nationalBase = ownership === "own" ? 2100 : 1400;
  const originAvg  = Math.round((nationalBase * originCOL / 100) / 50) * 50;
  const destAvg    = Math.round((nationalBase * destCOL   / 100) / 50) * 50;
  return { originAvg, destAvg, delta: destAvg - originAvg };
}

function getSmartTips(form, miles, isPeak) {
  const originCOL  = getCOL(form.origin);
  const destCOL    = getCOL(form.dest);
  const movingUp   = destCOL > originCOL + 5;
  const movingDown = destCOL < originCOL - 5;
  const isLong     = miles > 800;

  const pool = [
    isPeak && {
      icon: "📅",
      tip: `June–August moves cost 20–30% more. If your timeline has any flexibility, shifting even to late September can save hundreds.`
    },
    isLong && {
      icon: "📦",
      tip: `For a ${miles.toLocaleString()}-mile move, get at least one PODS or container quote alongside full-service movers — the gap can be $1,500 or more.`
    },
    !isLong && {
      icon: "🚛",
      tip: `Under 500 miles, a truck rental plus hired day labor often beats full-service movers by 40–60%. Worth getting both quotes before you commit.`
    },
    movingUp && {
      icon: "💡",
      tip: `${form.dest} has a meaningfully higher cost of living than ${form.origin}. Build a 3-month buffer into your budget — the adjustment period hits harder than most people expect.`
    },
    movingDown && {
      icon: "✅",
      tip: `You're moving into a lower-cost market. That monthly delta compounds — even modest savings on rent add up significantly over your first year.`
    },
    !movingUp && !movingDown && {
      icon: "⚖️",
      tip: `${form.origin} and ${form.dest} have similar costs of living. Your biggest financial variable is the move itself — get at least three quotes and negotiate.`
    },
    form.vehicleShip && {
      icon: "🚗",
      tip: `Book vehicle transport 4–6 weeks out. Last-minute bookings often cost 30–50% more, and your carrier options shrink fast.`
    },
    form.storage && {
      icon: "🏠",
      tip: `Reserve a climate-controlled unit before you arrive — storage demand spikes on common move-in dates and units fill up fast.`
    },
    form.ownership === "own" && {
      icon: "🏡",
      tip: `If you're selling in ${form.origin} and buying in ${form.dest}, get pre-approved in ${form.dest} before you list — financing requirements differ by state and it prevents delays at closing.`
    },
    form.ownership === "rent" && {
      icon: "📋",
      tip: `In competitive rental markets, showing up with 2–3 months of proof of income and a solid rental history letter moves you to the top of the stack.`
    },
    {
      icon: "📬",
      tip: `USPS mail forwarding takes 7–10 business days to activate. Set it up at least 2 weeks before your move date, not after.`
    },
  ].filter(Boolean);

  return pool.slice(0, 3);
}

function buildTasks(form) {
  const tasks = [...TASK_TEMPLATES.base];
  if (form.ownership === "own") tasks.push(...TASK_TEMPLATES.own);
  else tasks.push(...TASK_TEMPLATES.rent);
  if (form.vehicleShip) tasks.push(...TASK_TEMPLATES.vehicle);
  if (form.storage) tasks.push(...TASK_TEMPLATES.storage);
  return tasks.sort((a, b) => b.weeks - a.weeks);
}

// ─── SHARED STYLES ─────────────────────────────────────────────────────────────

const SHARED_CSS = `
  .card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 16px; padding: 2rem; box-shadow: 0 1px 4px rgba(0,0,0,0.05); }
  select, input { background: #F9FAFB; border: 1px solid #E5E7EB; color: #111827; border-radius: 8px; padding: .6rem 1rem; font-size: .95rem; font-family: 'DM Sans', sans-serif; width: 100%; outline: none; }
  select:focus, input:focus { border-color: #16A34A; box-shadow: 0 0 0 3px rgba(22,163,74,0.1); }
  label { font-family: 'DM Sans', sans-serif; font-size: .78rem; font-weight: 500; color: #6B7280; text-transform: uppercase; letter-spacing: .08em; display: block; margin-bottom: .4rem; }
  .toggle-btn { background: #F9FAFB; border: 1px solid #E5E7EB; color: #6B7280; border-radius: 8px; padding: .5rem 1.2rem; font-family: 'DM Sans', sans-serif; font-size: .9rem; cursor: pointer; transition: all .2s; }
  .toggle-btn.active { background: #DCFCE7; border-color: #86EFAC; color: #15803D; font-weight: 500; }
  .cta { background: #16A34A; color: #FFFFFF; border: none; border-radius: 10px; padding: .9rem 2.5rem; font-family: 'DM Sans', sans-serif; font-size: 1rem; font-weight: 600; cursor: pointer; letter-spacing: .02em; transition: all .2s; width: 100%; margin-top: 1.5rem; }
  .cta:hover { background: #15803D; }
  .cta:disabled { opacity: .4; cursor: not-allowed; }
  .back-btn { background: none; border: 1px solid #E5E7EB; color: #6B7280; border-radius: 10px; padding: .6rem 1.4rem; font-family: 'DM Sans', sans-serif; font-size: .85rem; cursor: pointer; margin-bottom: 1.5rem; transition: border-color .2s, color .2s; }
  .back-btn:hover { border-color: #16A34A; color: #16A34A; }
  .grid2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1rem; }
  .phase-header { display: flex; align-items: center; justify-content: space-between; padding: .8rem 1rem; border-radius: 10px; cursor: pointer; margin-bottom: .3rem; background: #FFFFFF; border: 1px solid #E5E7EB; transition: border-color .15s, box-shadow .15s; }
  .phase-header:hover { border-color: #D1D5DB; box-shadow: 0 1px 4px rgba(0,0,0,0.06); }
  .task-item { display: flex; align-items: flex-start; gap: .75rem; padding: .75rem 1rem .75rem 2.5rem; border-radius: 8px; cursor: pointer; transition: background .15s; }
  .task-item:hover { background: #F9FAFB; }
  .checkbox { width: 18px; height: 18px; border-radius: 4px; border: 2px solid #D1D5DB; flex-shrink: 0; margin-top: 2px; display: flex; align-items: center; justify-content: center; font-size: .7rem; transition: all .2s; }
  .checkbox.done { background: #16A34A; border-color: #16A34A; color: #fff; }
  .stat-card { background: #FFFFFF; border: 1px solid #E5E7EB; border-radius: 12px; padding: 1rem 1.5rem; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
  .cost-row { display: flex; justify-content: space-between; align-items: center; padding: .55rem 0; border-bottom: 1px solid #F3F4F6; font-family: 'DM Sans', sans-serif; font-size: .9rem; color: #374151; }
  .cost-row:last-child { border-bottom: none; }
  .tip-card { background: #F0FDF4; border: 1px solid #BBF7D0; border-radius: 10px; padding: 1rem 1.2rem; margin-bottom: .6rem; font-family: 'DM Sans', sans-serif; font-size: .88rem; line-height: 1.55; display: flex; gap: .75rem; align-items: flex-start; }
`;

const PAGE = {
  minHeight: "100vh",
  background: "#F8FAFC",
  color: "#111827",
  fontFamily: "'DM Sans', sans-serif",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  padding: "2rem",
};

// ─── APP ───────────────────────────────────────────────────────────────────────

export default function App() {
  const [step, setStep]                 = useState(1);
  const [form, setForm]                 = useState({
    origin: "", dest: "", bedrooms: "2", ownership: "rent",
    moveMonth: new Date().getMonth(), vehicleShip: false, storage: false
  });
  const [project, setProject]           = useState(null);
  const [expandedPhases, setExpandedPhases] = useState({});
  const [completedTasks, setCompletedTasks] = useState({});
  const [loading, setLoading]           = useState(false);

  const isPeak = PEAK_MONTHS.includes(parseInt(form.moveMonth));

  function handleCalculate() {
    setLoading(true);
    const tasks   = buildTasks(form);
    const miles   = getMiles(form.origin, form.dest);
    const costs   = estimateMovingCost(form.origin, form.dest, form.bedrooms, isPeak, form.vehicleShip, form.storage);
    const housing = getHousingDelta(form.origin, form.dest, form.ownership);
    const tips    = getSmartTips(form, miles, isPeak);
    setTimeout(() => {
      setProject({ tasks, costs, housing, tips });
      setLoading(false);
      setStep(2);
    }, 700);
  }

  function togglePhase(phase) {
    setExpandedPhases(p => ({ ...p, [phase]: !p[phase] }));
  }

  function toggleTask(id) {
    setCompletedTasks(p => ({ ...p, [id]: !p[id] }));
  }

  // ─── STEP 1: FORM ────────────────────────────────────────────────────────────

  if (step === 1) return (
    <div style={{ ...PAGE, justifyContent: "center" }}>
      <style>{SHARED_CSS}</style>

      <div style={{ maxWidth: 560, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: "2.5rem" }}>
          <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "2.4rem", fontWeight: 700, letterSpacing: "-.02em", lineHeight: 1.1 }}>
            Relocate
          </div>
          <div style={{ color: "#6B7280", fontSize: ".95rem", marginTop: ".5rem" }}>
            Your move. No spreadsheets.
          </div>
        </div>

        <div className="card">
          <div className="grid2">
            <div>
              <label>Moving From</label>
              <select value={form.origin} onChange={e => setForm(f => ({ ...f, origin: e.target.value }))}>
                <option value="">Select state</option>
                {STATES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label>Moving To</label>
              <select value={form.dest} onChange={e => setForm(f => ({ ...f, dest: e.target.value }))}>
                <option value="">Select state</option>
                {STATES.map(s => <option key={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div style={{ marginTop: "1.25rem" }}>
            <label>Home Size</label>
            <div style={{ display: "flex", gap: ".5rem" }}>
              {["1","2","3","4","5+"].map(b => (
                <button key={b} className={`toggle-btn${form.bedrooms === b ? " active" : ""}`}
                  onClick={() => setForm(f => ({ ...f, bedrooms: b }))}>
                  {b} bd
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginTop: "1.25rem" }}>
            <label>You Are Currently</label>
            <div style={{ display: "flex", gap: ".5rem" }}>
              {[["rent", "Renting"], ["own", "Owning"]].map(([v, l]) => (
                <button key={v} className={`toggle-btn${form.ownership === v ? " active" : ""}`}
                  onClick={() => setForm(f => ({ ...f, ownership: v }))} style={{ flex: 1 }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginTop: "1.25rem" }}>
            <label>Planned Move Month</label>
            <select value={form.moveMonth} onChange={e => setForm(f => ({ ...f, moveMonth: e.target.value }))}>
              {MOVE_MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
            </select>
            {isPeak && (
              <div style={{ color: "#D97706", fontSize: ".8rem", marginTop: ".4rem", background: "#FFFBEB", border: "1px solid #FDE68A", borderRadius: 6, padding: ".35rem .7rem", display: "inline-block" }}>
                ⚠ Peak season — expect ~30% higher moving costs
              </div>
            )}
          </div>

          <div style={{ marginTop: "1.25rem" }}>
            <label>Additional Needs</label>
            <div style={{ display: "flex", gap: ".5rem" }}>
              <button className={`toggle-btn${form.vehicleShip ? " active" : ""}`}
                onClick={() => setForm(f => ({ ...f, vehicleShip: !f.vehicleShip }))} style={{ flex: 1 }}>
                🚗 Ship Vehicle
              </button>
              <button className={`toggle-btn${form.storage ? " active" : ""}`}
                onClick={() => setForm(f => ({ ...f, storage: !f.storage }))} style={{ flex: 1 }}>
                📦 Need Storage
              </button>
            </div>
          </div>

          <button className="cta" disabled={!form.origin || !form.dest || loading} onClick={handleCalculate}>
            {loading ? "Building your moving project..." : "Calculate My Move →"}
          </button>
        </div>
      </div>
    </div>
  );

  // ─── STEP 2: DASHBOARD ───────────────────────────────────────────────────────

  const totalTasks = project?.tasks.length || 0;
  const doneTasks  = Object.values(completedTasks).filter(Boolean).length;
  const pct        = totalTasks ? Math.round((doneTasks / totalTasks) * 100) : 0;
  const phases     = project ? [...new Set(project.tasks.map(t => t.phase))] : [];

  return (
    <div style={{ ...PAGE, alignItems: "stretch" }}>
      <style>{SHARED_CSS}</style>

      <div style={{ maxWidth: 700, margin: "0 auto", width: "100%" }}>

        <button className="back-btn" onClick={() => { setStep(1); setProject(null); setCompletedTasks({}); }}>
          ← Start Over
        </button>

        {/* Header */}
        <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.8rem", marginBottom: ".25rem", color: "#111827" }}>
          {form.origin} → {form.dest}
        </div>
        <div style={{ color: "#6B7280", fontSize: ".9rem", marginBottom: "1.5rem" }}>
          Moving Project · {MOVE_MONTHS[form.moveMonth]} · {totalTasks} tasks · {project?.costs.miles.toLocaleString()} miles
        </div>

        {/* Progress */}
        <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12, padding: "1rem 1.5rem", marginBottom: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: ".85rem", marginBottom: ".6rem" }}>
            <span style={{ color: "#6B7280" }}>Progress</span>
            <span style={{ color: "#16A34A", fontWeight: 600 }}>{doneTasks} / {totalTasks} tasks · {pct}%</span>
          </div>
          <div style={{ background: "#F3F4F6", borderRadius: 999, height: 6, overflow: "hidden" }}>
            <div style={{ background: "#16A34A", height: "100%", width: `${pct}%`, borderRadius: 999, transition: "width .4s ease" }} />
          </div>
        </div>

        {/* Cost + Housing summary */}
        <div className="grid2" style={{ marginBottom: "1rem" }}>
          <div className="stat-card">
            <div style={{ fontSize: ".75rem", color: "#6B7280", textTransform: "uppercase", letterSpacing: ".08em" }}>Move Cost Est.</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", color: "#16A34A", marginTop: ".25rem" }}>{fmt(project.costs.total)}</div>
          </div>
          <div className="stat-card">
            <div style={{ fontSize: ".75rem", color: "#6B7280", textTransform: "uppercase", letterSpacing: ".08em" }}>Housing Delta</div>
            <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.5rem", color: project.housing.delta > 0 ? "#DC2626" : "#16A34A", marginTop: ".25rem" }}>
              {project.housing.delta > 0 ? "+" : "–"}{fmt(project.housing.delta)}/mo
            </div>
          </div>
        </div>

        {/* Cost breakdown */}
        <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12, padding: "1rem 1.5rem", marginBottom: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: ".75rem", color: "#6B7280", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".75rem" }}>
            Cost Breakdown
          </div>
          <div className="cost-row"><span>Moving truck / company</span><span style={{ color: "#16A34A", fontWeight: 600 }}>{fmt(project.costs.moving)}</span></div>
          <div className="cost-row"><span>Packing materials &amp; labor</span><span style={{ color: "#16A34A", fontWeight: 600 }}>{fmt(project.costs.packing)}</span></div>
          {form.vehicleShip && <div className="cost-row"><span>Vehicle shipping</span><span style={{ color: "#16A34A", fontWeight: 600 }}>{fmt(project.costs.vehicle)}</span></div>}
          {form.storage && <div className="cost-row"><span>Storage (first month)</span><span style={{ color: "#16A34A", fontWeight: 600 }}>{fmt(project.costs.storage)}</span></div>}
          {isPeak && <div className="cost-row"><span style={{ color: "#D97706" }}>⚠ Peak season surcharge included</span><span style={{ color: "#D97706" }}>~30%</span></div>}
          <div className="cost-row" style={{ marginTop: ".5rem", paddingTop: ".75rem", borderTop: "1px solid #E5E7EB", borderBottom: "none" }}>
            <span style={{ fontWeight: 700, color: "#111827" }}>Total Estimated</span>
            <span style={{ color: "#16A34A", fontWeight: 700, fontSize: "1.05rem" }}>{fmt(project.costs.total)}</span>
          </div>
        </div>

        {/* Housing delta detail */}
        <div style={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12, padding: "1rem 1.5rem", marginBottom: "1rem", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" }}>
          <div style={{ fontSize: ".75rem", color: "#6B7280", textTransform: "uppercase", letterSpacing: ".08em", marginBottom: ".75rem" }}>
            Housing Cost Shift
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ color: "#6B7280", fontSize: ".8rem" }}>{form.origin}</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.4rem", color: "#111827", marginTop: ".2rem" }}>
                {fmt(project.housing.originAvg)}<span style={{ fontSize: ".8rem", color: "#9CA3AF" }}>/mo</span>
              </div>
            </div>
            <div style={{ color: "#16A34A", fontSize: "1.2rem" }}>→</div>
            <div style={{ textAlign: "center", flex: 1 }}>
              <div style={{ color: "#6B7280", fontSize: ".8rem" }}>{form.dest}</div>
              <div style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.4rem", color: project.housing.delta > 0 ? "#DC2626" : "#16A34A", marginTop: ".2rem" }}>
                {fmt(project.housing.destAvg)}<span style={{ fontSize: ".8rem", color: "#9CA3AF" }}>/mo</span>
              </div>
            </div>
          </div>
          <div style={{ textAlign: "center", marginTop: ".75rem", fontSize: ".88rem", color: project.housing.delta > 0 ? "#DC2626" : "#16A34A" }}>
            {project.housing.delta > 0
              ? `↑ ~${fmt(project.housing.delta)}/mo more expensive`
              : `↓ ~${fmt(Math.abs(project.housing.delta))}/mo less expensive`}
          </div>
        </div>

        {/* Smart tips */}
        {project.tips.length > 0 && (
          <div style={{ marginBottom: "1.5rem" }}>
            <div style={{ fontSize: ".75rem", color: "#6B7280", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: ".75rem" }}>
              Tips for Your Move
            </div>
            {project.tips.map((tip, i) => (
              <div key={i} className="tip-card">
                <span style={{ fontSize: "1.1rem", lineHeight: 1.4 }}>{tip.icon}</span>
                <span style={{ color: "#374151" }}>{tip.tip}</span>
              </div>
            ))}
          </div>
        )}

        {/* Checklist */}
        <div style={{ fontSize: ".75rem", color: "#6B7280", textTransform: "uppercase", letterSpacing: ".1em", marginBottom: ".75rem" }}>
          Your Moving Checklist
        </div>

        {phases.map(phase => {
          const phaseTasks = project.tasks.filter(t => t.phase === phase);
          const phaseColor = PHASE_COLORS[phase] || "#888";
          const allDone    = phaseTasks.every(t => completedTasks[t.id]);
          const isOpen     = expandedPhases[phase] !== false;

          return (
            <div key={phase} style={{ marginBottom: ".5rem" }}>
              <div className="phase-header" onClick={() => togglePhase(phase)}>
                <div style={{ display: "flex", alignItems: "center", gap: ".75rem" }}>
                  <div style={{ width: 10, height: 10, borderRadius: "50%", background: phaseColor, flexShrink: 0 }} />
                  <span style={{ fontWeight: 600, fontSize: ".95rem", color: "#111827" }}>{phase}</span>
                  <span style={{ fontSize: ".8rem", color: "#9CA3AF" }}>
                    {phaseTasks.filter(t => completedTasks[t.id]).length}/{phaseTasks.length}
                  </span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
                  {allDone && <span style={{ fontSize: ".75rem", color: "#16A34A", fontWeight: 500 }}>✓ Done</span>}
                  <span style={{ color: "#9CA3AF", fontSize: ".85rem" }}>{isOpen ? "▲" : "▼"}</span>
                </div>
              </div>

              {isOpen && phaseTasks.map(task => (
                <div key={task.id} className="task-item" onClick={() => toggleTask(task.id)}>
                  <div className={`checkbox${completedTasks[task.id] ? " done" : ""}`}>
                    {completedTasks[task.id] && "✓"}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: ".9rem", fontWeight: 500, textDecoration: completedTasks[task.id] ? "line-through" : "none", color: completedTasks[task.id] ? "#9CA3AF" : "#111827" }}>
                      {task.title}
                    </div>
                    <div style={{ fontSize: ".8rem", color: "#6B7280", marginTop: ".15rem" }}>
                      {task.detail}
                    </div>
                    <div style={{ fontSize: ".75rem", color: phaseColor, marginTop: ".2rem" }}>
                      {task.weeks > 0 ? `${task.weeks} weeks before move` : task.weeks === 0 ? "Move day" : `${Math.abs(task.weeks)} weeks after move`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          );
        })}

        <div style={{ textAlign: "center", color: "#9CA3AF", fontSize: ".75rem", marginTop: "2rem", paddingTop: "1rem", borderTop: "1px solid #E5E7EB" }}>
          Cost estimates are ballparks based on state-level data. Actual costs vary by city, carrier, and timing.
        </div>
      </div>
    </div>
  );
}
